import React, { useEffect, useState } from 'react';
import { Percent, TrendingUp, Building2 } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { cbuApi } from '../../../services/api';
import type { InterestRatesData } from '../../../services/api/cbuApi';

interface InterestRatesPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

export const InterestRatesPanel: React.FC<InterestRatesPanelProps> = ({
  isMinimized,
  onMinimize,
}) => {
  const [data, setData] = useState<InterestRatesData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const response = await cbuApi.getInterestRates();
      if (response.data) {
        setData(response.data);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const exportData = data ? [
    { indicator: 'Policy Rate', value: data.policyRate, unit: '%' },
    { indicator: 'Deposit Rate', value: data.depositRateAvg, unit: '%' },
    { indicator: 'Lending Rate', value: data.lendingRateAvg, unit: '%' },
    { indicator: 'Interbank Rate', value: data.interbankRate, unit: '%' },
  ] : [];

  return (
    <Panel
      id="interest-rates"
      title="Interest Rates"
      subtitle="CBU"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      data={exportData}
    >
      <div className="h-full flex flex-col p-3">
        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
            Loading interest rates...
          </div>
        ) : data ? (
          <>
            {/* HERO - Policy Rate */}
            <div className="text-center p-4 bg-gradient-to-b from-data-amber/10 to-transparent rounded-lg border border-data-amber/20 mb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Building2 size={16} className="text-data-amber" />
                <span className="text-xs text-data-amber/80 uppercase tracking-widest font-semibold">
                  CBU Policy Rate
                </span>
              </div>

              {/* GIANT Policy Rate Number */}
              <div className="text-5xl md:text-6xl font-mono font-black text-data-amber tracking-tight">
                {data.policyRate.toFixed(1)}
                <span className="text-3xl md:text-4xl">%</span>
              </div>

              {/* Status Badge */}
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 bg-terminal-border/50 rounded-full">
                <span className="text-xs text-gray-400">Maintained</span>
                <span className="text-xs text-white font-medium">Dec 11, 2025</span>
              </div>
            </div>

            {/* Secondary Rates - 3 Column Grid */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {/* Deposit Rate */}
              <div className="p-3 bg-terminal-border/30 rounded-lg text-center">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Deposit</div>
                <div className="text-xl md:text-2xl font-mono font-bold text-white">
                  {data.depositRateAvg.toFixed(1)}%
                </div>
                <div className="text-xs text-data-green mt-1">
                  +{(data.depositRateAvg - data.policyRate).toFixed(1)}% spread
                </div>
              </div>

              {/* Lending Rate */}
              <div className="p-3 bg-terminal-border/30 rounded-lg text-center">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Lending</div>
                <div className="text-xl md:text-2xl font-mono font-bold text-white">
                  {data.lendingRateAvg.toFixed(1)}%
                </div>
                <div className="text-xs text-data-amber mt-1">
                  +{(data.lendingRateAvg - data.policyRate).toFixed(1)}% spread
                </div>
              </div>

              {/* Interbank Rate */}
              <div className="p-3 bg-terminal-border/30 rounded-lg text-center">
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Interbank</div>
                <div className="text-xl md:text-2xl font-mono font-bold text-white">
                  {data.interbankRate.toFixed(1)}%
                </div>
                <div className="text-xs text-data-blue mt-1">
                  {(data.interbankRate - data.policyRate).toFixed(1)}% vs policy
                </div>
              </div>
            </div>

            {/* Rate Corridor Visual */}
            <div className="p-3 bg-terminal-border/20 rounded-lg">
              <div className="text-xs text-gray-500 mb-2">Interest Rate Corridor</div>
              <div className="relative h-3 bg-terminal-border rounded-full overflow-hidden">
                {/* Policy Rate Marker */}
                <div
                  className="absolute top-0 bottom-0 w-1 bg-data-amber z-10"
                  style={{ left: `${(data.policyRate / 30) * 100}%` }}
                />
                {/* Interbank */}
                <div
                  className="absolute top-0 bottom-0 w-0.5 bg-data-blue/70"
                  style={{ left: `${(data.interbankRate / 30) * 100}%` }}
                />
                {/* Deposit */}
                <div
                  className="h-full bg-data-green/40"
                  style={{ width: `${(data.depositRateAvg / 30) * 100}%` }}
                />
              </div>
              <div className="flex justify-between mt-1.5 text-xs">
                <span className="text-gray-600">0%</span>
                <div className="flex items-center gap-3">
                  <span className="text-data-amber">Policy {data.policyRate}%</span>
                  <span className="text-data-green">Deposit {data.depositRateAvg}%</span>
                  <span className="text-data-amber">Lending {data.lendingRateAvg}%</span>
                </div>
                <span className="text-gray-600">30%</span>
              </div>
            </div>
          </>
        ) : null}

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
          <DataSourceBadge source={DATA_SOURCES.CBU} showLabel size="xs" />
          <span className="text-xs text-gray-600">{data?.lastUpdated}</span>
        </div>
      </div>
    </Panel>
  );
};

export default InterestRatesPanel;
